/**
 * O "Incluir Json" do sistema antigo, na aba Importar: ler o JSON colado e
 * traduzir para os itens que `api/pinouts/importar-json` grava.
 *
 * O JSON que a equipe cola é o do sistema antigo —
 * `{"pinouts":[{"component_codigo":"B001","terminal_pino":"1",…}]}` — e a rota
 * nova espera os mesmos campos em camelCase (`componentCodigo`). Colado direto,
 * nenhum campo casaria e a API responderia "0 criados". A tradução mora aqui, na
 * tela; a API não muda (decisão de 28/09/2026, Q2).
 *
 * - Aceita o objeto com `pinouts` ou a lista solta `[ … ]`.
 * - O nome do campo é conferido sem maiúscula e sem `_`: `component_codigo` e
 *   `componentCodigo` são o mesmo campo.
 * - Número vira texto (`"terminal_pino": 1`).
 * - Campo que não é do pinout não é gravado, e é listado — para o erro de
 *   digitação (`componente_codigo`) não sumir calado.
 * - A linha sem um dos cinco campos que a API exige é recusada aqui, com o
 *   motivo, antes da viagem (a mesma regra do `Mapear` do ImportacaoPinoutService).
 * - A linha com um campo maior que a coluna do banco também é recusada aqui,
 *   com o campo e o tamanho.
 *
 * Sem import de runtime: é função pura, testada sem navegador e sem API.
 */

import type { ItemDeImportacao } from "@/api/importacao"

type Chave = keyof ItemDeImportacao

/** O nome do campo, sem maiúscula e sem `_`, para o campo do item. */
const CAMPOS: Record<string, Chave> = {
  componentcodigo: "componentCodigo",
  componentdescricao: "componentDescricao",
  conector: "conector",
  terminalpino: "terminalPino",
  circuito: "circuito",
  cor: "cor",
  chicote: "chicote",
  restricao: "restricao",
  obs: "obs",
}

/** Os cinco sem os quais a API recusa a linha. */
const OBRIGATORIOS: [Chave, string][] = [
  ["componentCodigo", "componente (código)"],
  ["componentDescricao", "componente (descrição)"],
  ["conector", "conector"],
  ["terminalPino", "pino"],
  ["circuito", "circuito"],
]

/**
 * O tamanho de cada campo na coluna do banco (os mesmos limites do
 * `PinoutService.Tamanhos` da API). A API recusa a linha que passa; conferir
 * aqui é para quem colou ver isso antes da viagem, com os outros motivos.
 */
const LIMITES: [Chave, string, number][] = [
  ["componentCodigo", "componente (código)", 100],
  ["componentDescricao", "componente (descrição)", 100],
  ["conector", "conector", 100],
  ["terminalPino", "pino", 100],
  ["circuito", "circuito", 100],
  ["cor", "cor", 100],
  ["chicote", "chicote", 100],
  ["restricao", "restrição", 255],
  ["obs", "observação", 255],
]

/** `item` é a posição na lista do JSON, contando de 1. */
export type RecusaDoJson = { item: number; motivo: string }

export type JsonLido =
  | {
      ok: true
      /** Quantos itens a lista tem. */
      total: number
      /** Os que vão para a API. */
      itens: ItemDeImportacao[]
      /** A posição (0-based) de cada item na lista original — o índice que a API devolve é o de `itens`. */
      posicoes: number[]
      recusadas: RecusaDoJson[]
      /** Os nomes de campo que não são do pinout, como vieram. */
      desconhecidos: string[]
    }
  | { ok: false; erro: string }

const normalizar = (nome: string) => nome.replace(/_/g, "").toLowerCase()

export function lerJsonDePinout(texto: string): JsonLido {
  const limpo = texto.replace(/^\uFEFF/, "")
  let dado: unknown
  try {
    dado = JSON.parse(limpo)
  } catch (e) {
    return { ok: false, erro: ondeQuebrou(limpo, e) }
  }

  const lista = Array.isArray(dado) ? dado : ehObjeto(dado) ? pinoutsDe(dado) : null
  if (!lista) return { ok: false, erro: 'O JSON precisa ser {"pinouts": [ … ]} ou uma lista [ … ].' }
  if (lista.length === 0) return { ok: false, erro: "O JSON não tem nenhum pinout." }

  const itens: ItemDeImportacao[] = []
  const posicoes: number[] = []
  const recusadas: RecusaDoJson[] = []
  const desconhecidos = new Set<string>()

  lista.forEach((bruto, i) => {
    if (!ehObjeto(bruto)) {
      recusadas.push({ item: i + 1, motivo: "não é um pinout { … }." })
      return
    }
    const item: ItemDeImportacao = {
      componentCodigo: "", componentDescricao: "", conector: "", terminalPino: "",
      circuito: "", cor: "", chicote: "", restricao: "", obs: "",
    }
    for (const [nome, valor] of Object.entries(bruto)) {
      const chave = CAMPOS[normalizar(nome)]
      if (chave) item[chave] = comoTexto(valor)
      else desconhecidos.add(nome)
    }
    const faltando = OBRIGATORIOS.filter(([c]) => item[c] === "").map(([, rotulo]) => rotulo)
    if (faltando.length > 0) {
      recusadas.push({ item: i + 1, motivo: `faltou ${faltando.join(", ")}.` })
      return
    }
    const longo = LIMITES.find(([c, , max]) => item[c].length > max)
    if (longo) {
      const [c, rotulo, max] = longo
      recusadas.push({ item: i + 1, motivo: `${rotulo} passa de ${max} caracteres (tem ${item[c].length}).` })
      return
    }
    itens.push(item)
    posicoes.push(i)
  })

  return { ok: true, total: lista.length, itens, posicoes, recusadas, desconhecidos: [...desconhecidos].sort() }
}

function ehObjeto(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

/** A lista em `pinouts` (com qualquer caixa). */
function pinoutsDe(o: Record<string, unknown>): unknown[] | null {
  const chave = Object.keys(o).find((k) => normalizar(k) === "pinouts")
  const lista = chave === undefined ? undefined : o[chave]
  return Array.isArray(lista) ? lista : null
}

function comoTexto(v: unknown): string {
  if (typeof v === "string") return v.trim()
  if (typeof v === "number" || typeof v === "boolean") return String(v)
  return ""
}

/**
 * A mensagem do `JSON.parse` com linha e coluna. O Chrome diz a posição em
 * caracteres ("at position 120"), o Firefox diz "line 3 column 5"; quem colou
 * pensa em linha.
 */
function ondeQuebrou(texto: string, e: unknown): string {
  const aspasCurvas = /[\u201C\u201D]/.test(texto)
    ? " As aspas curvas “ ” não valem em JSON — troque por \"."
    : ""
  const msg = e instanceof Error ? e.message : String(e)
  const pos = msg.match(/position (\d+)/)
  const lc = msg.match(/line (\d+) column (\d+)/)
  let p: number
  if (pos) p = Number(pos[1])
  else if (lc) {
    const linhas = texto.split("\n")
    p = linhas.slice(0, Number(lc[1]) - 1).reduce((s, l) => s + l.length + 1, 0) + Number(lc[2]) - 1
  } else if (/end of (JSON )?(input|data)|Unexpected end/i.test(msg)) {
    return "O JSON termina antes da hora — falta fechar } ou ]." + aspasCurvas
  } else {
    return `O JSON não é válido (${msg}).` + aspasCurvas
  }
  const antes = texto.slice(0, p).split("\n")
  const linha = antes.length
  const coluna = antes[antes.length - 1]!.length + 1
  const perto = texto.slice(Math.max(0, p - 20), p + 20).replace(/\s+/g, " ").trim()
  return `O JSON está quebrado na linha ${linha}, coluna ${coluna}, perto de: ${perto}` + aspasCurvas
}
